import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Wand2, Trash2, BookmarkPlus, Bookmark, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EXAMPLE = `Lunedì 10/03 - Preparazione GPG - 90 min
- Riscaldamento 15 min
- Gambe-scherma 20 min passi e affondi
- Tecnica 25 min affondo su bersaglio
- Assalti 25 min 5 stoccate
- Defaticamento 5 min`;

const KIND_LABELS = {
  riscaldamento: "Riscaldamento", mobilita: "Mobilità", preparazione: "Preparazione",
  footwork: "Gambe-scherma", tecnica: "Tecnica", tattica: "Tattica", coppia: "Coppia",
  lezione: "Lezione", assalti: "Assalti", defaticamento: "Defaticamento",
};

export default function TrainingTextImportDialog({ onDone, groups = [] }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("text"); // text | ai
  const [text, setText] = useState("");
  const [aiObjective, setAiObjective] = useState("");
  const [aiStartDate, setAiStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [aiGroup, setAiGroup] = useState("");
  const [aiDays, setAiDays] = useState(6);
  const [aiProfile, setAiProfile] = useState("progressivo");
  const [parsed, setParsed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");

  useEffect(() => { if (open) api.get("/training-templates").then((r) => setTemplates(r.data)); }, [open]);

  const reset = () => { setText(""); setParsed(null); setAiObjective(""); };

  const doParseText = async () => {
    if (!text.trim()) return toast.error("Testo richiesto");
    setLoading(true);
    try {
      const { data } = await api.post("/trainings/import/text/parse", { text });
      const list = (data.trainings || []).map((t) => ({ ...t, keep: true, group_id: "" }));
      setParsed(list);
      toast.success(`${list.length} allenamenti rilevati`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore parsing"); }
    finally { setLoading(false); }
  };

  const doGenerateAI = async () => {
    if (!aiObjective.trim()) return toast.error("Descrivi l'obiettivo");
    setLoading(true);
    try {
      const { data } = await api.post("/trainings/ai-weekly", {
        objective: aiObjective, start_date: aiStartDate, days: aiDays,
        group_id: aiGroup || null, intensity_profile: aiProfile,
      });
      const list = (data.trainings || []).map((t) => ({ ...t, keep: true, group_id: aiGroup || "" }));
      setParsed(list);
      toast.success(`${list.length} sessioni generate dall'AI`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore AI"); }
    finally { setLoading(false); }
  };

  const doSave = async () => {
    const kept = parsed.filter((t) => t.keep).map((t) => ({
      ...t, group_ids: t.group_id ? [t.group_id] : [],
    }));
    if (kept.length === 0) return toast.error("Seleziona almeno un allenamento");
    try {
      const { data } = await api.post("/trainings/import/text/save", { trainings: kept });
      toast.success(`${data.saved} allenamenti creati`);
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const upd = (i, patch) => setParsed((arr) => { const a = [...arr]; a[i] = { ...a[i], ...patch }; return a; });
  const remove = (i) => setParsed((arr) => arr.filter((_, idx) => idx !== i));
  const updBlock = (ti, bi, patch) => setParsed((arr) => {
    const a = [...arr]; const bl = [...a[ti].blocks]; bl[bi] = { ...bl[bi], ...patch };
    a[ti] = { ...a[ti], blocks: bl }; return a;
  });
  const removeBlock = (ti, bi) => setParsed((arr) => {
    const a = [...arr]; a[ti] = { ...a[ti], blocks: a[ti].blocks.filter((_, i) => i !== bi) }; return a;
  });

  const loadTemplate = (tpl) => {
    setText(tpl.text);
    setTab("text");
    setParsed(null);
    toast.success(`Template "${tpl.name}" caricato`);
  };
  const saveTemplate = async () => {
    if (!tplName.trim() || !text.trim()) return toast.error("Nome e testo richiesti");
    try {
      await api.post("/training-templates", { name: tplName.trim(), text });
      toast.success("Template salvato");
      const r = await api.get("/training-templates");
      setTemplates(r.data);
      setSaveTplOpen(false); setTplName("");
    } catch { toast.error("Errore"); }
  };
  const deleteTpl = async (id) => {
    if (!window.confirm("Eliminare il template?")) return;
    await api.delete(`/training-templates/${id}`);
    setTemplates(templates.filter((t) => t.id !== id));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>
        <Button data-testid="training-text-import-btn" variant="outline" className="h-12"><Wand2 className="w-4 h-4 mr-2" />Importa / Genera</Button>
      </div>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#0052FF]" />Allenamenti — Importa da testo o Genera con AI</DialogTitle>
          <DialogDescription>Incolla un testo, usa un template salvato o descrivi un obiettivo settimanale.</DialogDescription>
        </DialogHeader>

        {!parsed && (
          <div className="space-y-4 py-2">
            <div className="flex gap-2 border-b border-zinc-200 pb-2">
              <button onClick={() => setTab("text")} data-testid="tab-text" className={`px-4 py-2 text-sm font-semibold rounded-t-md ${tab === "text" ? "text-black border-b-2 border-black -mb-[9px]" : "text-zinc-500"}`}>Testo libero</button>
              <button onClick={() => setTab("ai")} data-testid="tab-ai-weekly" className={`px-4 py-2 text-sm font-semibold rounded-t-md ${tab === "ai" ? "text-black border-b-2 border-black -mb-[9px]" : "text-zinc-500"}`}>Programma AI settimanale</button>
            </div>

            {tab === "text" && (
              <div className="space-y-3">
                {templates.length > 0 && (
                  <div>
                    <Label>Template salvati</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {templates.map((t) => (
                        <div key={t.id} className="flex items-center gap-1 bg-zinc-100 rounded-full text-xs pl-3 pr-1 py-1">
                          <button onClick={() => loadTemplate(t)} className="hover:underline"><Bookmark className="w-3 h-3 inline mr-1" />{t.name}</button>
                          <button onClick={() => deleteTpl(t.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Testo degli allenamenti</Label>
                    {text.trim() && <button onClick={() => setSaveTplOpen(true)} className="text-xs text-[#0052FF] hover:underline"><BookmarkPlus className="w-3 h-3 inline mr-1" />Salva come template</button>}
                  </div>
                  <Textarea data-testid="training-text-input" rows={12} value={text} onChange={(e) => setText(e.target.value)} placeholder={EXAMPLE} className="font-mono text-sm" />
                  <div className="text-xs text-zinc-500 mt-1">Formato libero. L'AI capisce elenchi e date come "10/03" o "lunedì". <button onClick={() => setText(EXAMPLE)} className="text-[#0052FF] hover:underline">Usa esempio</button></div>
                </div>
              </div>
            )}

            {tab === "ai" && (
              <div className="space-y-3">
                <div>
                  <Label>Obiettivo settimanale</Label>
                  <Textarea data-testid="ai-objective" rows={3} value={aiObjective} onChange={(e) => setAiObjective(e.target.value)} placeholder='Es: "Preparazione al GPG di Palermo, focus sulla velocità di gambe e tempi di attacco per gli under 14"' />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div><Label>Inizio (Lun)</Label><Input type="date" value={aiStartDate} onChange={(e) => setAiStartDate(e.target.value)} /></div>
                  <div><Label>Giorni</Label><Input type="number" min={1} max={7} value={aiDays} onChange={(e) => setAiDays(parseInt(e.target.value || 6))} /></div>
                  <div>
                    <Label>Gruppo</Label>
                    <Select value={aiGroup || "none"} onValueChange={(v) => setAiGroup(v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="ai-group"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessuno</SelectItem>
                        {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Profilo intensità</Label>
                    <Select value={aiProfile} onValueChange={setAiProfile}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="progressivo">Progressivo (picco mid-week)</SelectItem>
                        <SelectItem value="costante">Costante</SelectItem>
                        <SelectItem value="picco">Picco + scarico pre-gara</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {parsed && (
          <div className="space-y-3 py-2">
            <div className="text-sm">{parsed.filter((t) => t.keep).length}/{parsed.length} selezionati</div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto no-scrollbar">
              {parsed.map((t, i) => (
                <div key={i} className={`bg-white border rounded-md p-4 ${t.keep ? "border-zinc-200" : "border-zinc-100 opacity-50"}`} data-testid={`preview-training-${i}`}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={t.keep} onChange={(e) => upd(i, { keep: e.target.checked })} className="mt-2" />
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-12 gap-2">
                        <Input className="col-span-4 h-9" value={t.title} onChange={(e) => upd(i, { title: e.target.value })} />
                        <Input className="col-span-2 h-9" type="date" value={t.date} onChange={(e) => upd(i, { date: e.target.value })} />
                        <div className="col-span-2 flex items-center gap-1">
                          <Input type="number" className="h-9 font-mono" value={t.duration} onChange={(e) => upd(i, { duration: parseInt(e.target.value || 0) })} />
                          <span className="text-xs text-zinc-500">min</span>
                        </div>
                        <select className="col-span-2 h-9 border border-zinc-200 rounded-md text-sm px-2" value={t.intensity} onChange={(e) => upd(i, { intensity: e.target.value })}>
                          <option value="bassa">Bassa</option>
                          <option value="media">Media</option>
                          <option value="alta">Alta</option>
                        </select>
                        <select data-testid={`preview-group-${i}`} className="col-span-2 h-9 border border-zinc-200 rounded-md text-sm px-2" value={t.group_id || ""} onChange={(e) => upd(i, { group_id: e.target.value })}>
                          <option value="">— Gruppo —</option>
                          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                      </div>
                      {t.objective && <Input className="h-8 text-xs" placeholder="Obiettivo" value={t.objective || ""} onChange={(e) => upd(i, { objective: e.target.value })} />}
                      <div className="space-y-1 pl-4 border-l-2 border-zinc-100">
                        {t.blocks.map((b, bi) => (
                          <div key={bi} className="flex items-center gap-2 text-xs">
                            <span className="text-[9px] uppercase tracking-wider bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded font-mono w-24 text-center">{KIND_LABELS[b.kind] || b.kind}</span>
                            <Input className="h-7 text-xs flex-1" value={b.title} onChange={(e) => updBlock(i, bi, { title: e.target.value })} />
                            <Input type="number" className="h-7 text-xs w-16 font-mono" value={b.duration} onChange={(e) => updBlock(i, bi, { duration: parseInt(e.target.value || 0) })} />
                            <button onClick={() => removeBlock(i, bi)} className="text-zinc-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => remove(i)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {!parsed ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              {tab === "text" ? (
                <Button data-testid="training-text-parse" onClick={doParseText} disabled={!text.trim() || loading} className="bg-black hover:bg-zinc-800">
                  {loading ? "Analisi AI…" : <><Sparkles className="w-4 h-4 mr-2" />Analizza con AI</>}
                </Button>
              ) : (
                <Button data-testid="ai-weekly-generate" onClick={doGenerateAI} disabled={!aiObjective.trim() || loading} className="bg-black hover:bg-zinc-800">
                  {loading ? "Generazione…" : <><Sparkles className="w-4 h-4 mr-2" />Genera settimana</>}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset}>Ricarica</Button>
              <Button data-testid="training-text-save" onClick={doSave} className="bg-black hover:bg-zinc-800">Salva {parsed.filter((t) => t.keep).length} allenamenti</Button>
            </>
          )}
        </DialogFooter>

        <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Salva come template</DialogTitle></DialogHeader>
            <div className="space-y-2 py-2">
              <Label>Nome template</Label>
              <Input data-testid="tpl-name" placeholder='es. "Lunedì Under 14"' value={tplName} onChange={(e) => setTplName(e.target.value)} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaveTplOpen(false)}>Annulla</Button>
              <Button data-testid="tpl-save" onClick={saveTemplate} className="bg-black hover:bg-zinc-800">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
